import React from 'react';
import { formColumnsWidth } from '../../../pageForm/PageHeadersWidthConfig';
import {
  IDataModelAddFieldInput,
  IDataModelExtendFactFieldInput,
} from '../../../../utils/apollo/graphqlSchema';
import {
  dataModelerRemoveDraftFieldData,
  dataModelerUpdateDraftFieldData,
} from '../../../../redux/slices/dataModeler';
import EditableTextArea from '../../../../components/EditableTextArea';
import { entityDescriptionSchema } from '../../../../components/EditableTextArea/validation';
import GeneralFormItem from '../../../../components/GeneralFormItem';
import TickCheckbox from '../../../../components/Checkbox/TickCheckbox';
import { useAppDispatch } from '../../../../redux/hooks';
import { useViewColumnsValidator } from '../validation';
import KubitTooltip from '../../../../components/KubitTooltip';
import {
  ActionIcon,
  CenteredDiv,
  IconsRowDiv,
  StyledFormUIRow,
} from '../DataModelerCommonUI';
import { SvgIcon } from '../../../../components/Icon';
import { dataModelTooltipOptions } from '../../../../components/KubitTooltip/globalTooltips';
import { colors } from '../../../../config/styles';
import DataModelerFieldGridTableColumns from './DataModelerFieldGridTableColumns';
import { fieldTypeName } from '../utils';

interface IProps {
  schemaKey: string;
  field: IDataModelAddFieldInput;
  isRequired: boolean;
  extendInputs: IDataModelExtendFactFieldInput[];
}

const DataModelerAddedFieldRow = ({
  schemaKey,
  field,
  extendInputs,
  isRequired,
}: IProps) => {
  const dispatch = useAppDispatch();
  const {
    allOtherColumnsSet,
    // columnValidator,
    // allOtherFieldKeysSet,
    allOtherFieldNamesSet,
    namesValidator,
  } = useViewColumnsValidator({
    viewKey: field.viewKey,
    tableKey: field.tableKey,
    tableColumn: field.column,
  });

  const isDuplicateViewColumnError = allOtherColumnsSet.has(field.viewColumn);
  // const isDuplicateFieldKeyError = allOtherFieldKeysSet.has(field.key);
  const isFieldNameError = allOtherFieldNamesSet.has(
    field.name ?? field.viewColumn,
  );
  const isFieldError = isDuplicateViewColumnError || isFieldNameError;
  const handleNameChange = (newValue: string) => {
    dispatch(
      dataModelerUpdateDraftFieldData({
        viewKey: field.viewKey,
        tableKey: field.tableKey,
        column: field.column,
        input: {
          name: newValue,
        },
      }),
    );
  };

  const handleDescriptionChange = (newValue: string) => {
    dispatch(
      dataModelerUpdateDraftFieldData({
        viewKey: field.viewKey,
        tableKey: field.tableKey,
        column: field.column,
        input: {
          description: newValue,
        },
      }),
    );
  };

  const handleFilterClick = () => {
    dispatch(
      dataModelerUpdateDraftFieldData({
        viewKey: field.viewKey,
        tableKey: field.tableKey,
        column: field.column,
        input: { filter: !field.filter },
      }),
    );
  };

  const handleGroupbyClick = () => {
    dispatch(
      dataModelerUpdateDraftFieldData({
        viewKey: field.viewKey,
        tableKey: field.tableKey,
        column: field.column,
        input: { groupby: !field.groupby },
      }),
    );
  };

  const handleRemoveClick = () => {
    if (!isRequired) {
      dispatch(
        dataModelerRemoveDraftFieldData({
          tableKey: field.tableKey,
          viewKey: field.viewKey,
          column: field.column,
        }),
      );
    }
  };

  return (
    <StyledFormUIRow
      $isHighlighted={true}
      $gridTemplateColumns={formColumnsWidth.dataModelerFieldsEditor}
    >
      <GeneralFormItem
        key={'class'}
        label={field.typeInput.fieldClass}
        isDisabled={false}
        isBold={true}
      />
      <EditableTextArea
        isBold
        value={field.name ?? ''}
        disabled={false}
        placeholder={''}
        onChange={handleNameChange}
        defaultRows={2}
        validation={namesValidator}
      />
      <EditableTextArea
        isBold
        value={field.description ?? ''}
        disabled={false}
        placeholder={''}
        onChange={handleDescriptionChange}
        defaultRows={4}
        validation={entityDescriptionSchema}
      />
      <GeneralFormItem
        key={'viewColumn'}
        label={field.viewColumn}
        tooltipContent={field.viewColumn}
        isDisabled={false}
        isBold={true}
      />
      <DataModelerFieldGridTableColumns
        isBold={true}
        schemaKey={schemaKey}
        existing={[]}
        added={field}
        extended={extendInputs}
      />
      <GeneralFormItem
        key={'type'}
        label={fieldTypeName(field.typeInput.type)}
        tooltipContent={fieldTypeName(field.typeInput.type)}
      />
      <CenteredDiv>
        <KubitTooltip
          {...dataModelTooltipOptions.filterCheckboxTooltipContent}
          noOpenDelay={true}
        >
          <TickCheckbox checked={field.filter} onChange={handleFilterClick} />
        </KubitTooltip>
      </CenteredDiv>
      <CenteredDiv>
        <KubitTooltip
          {...dataModelTooltipOptions.groupbyCheckboxTooltipContent}
          noOpenDelay={true}
        >
          <TickCheckbox checked={field.groupby} onChange={handleGroupbyClick} />
        </KubitTooltip>
      </CenteredDiv>
      <CenteredDiv>
        <KubitTooltip
          placement="top-start"
          noOpenDelay={true}
          content={
            isRequired
              ? 'This field is required and can not be removed'
              : isFieldError
                ? 'This field definition has errors. Click to remove'
                : 'Remove'
          }
        >
          <div onClick={handleRemoveClick}>
            {isFieldError && <ActionIcon name={'error-icon'} />}
            <ActionIcon
              name="icon-x"
              width={8}
              height={8}
              isDisabled={isRequired}
            />
          </div>
        </KubitTooltip>
      </CenteredDiv>
      <KubitTooltip
        {...dataModelTooltipOptions.addedFieldIcon}
        noOpenDelay={true}
      >
        <IconsRowDiv>
          <SvgIcon
            name={'circle'}
            fill={colors.normalGreen}
            height={12}
            width={12}
          />
        </IconsRowDiv>
      </KubitTooltip>
    </StyledFormUIRow>
  );
};

export default DataModelerAddedFieldRow;
